import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { format, eachDayOfInterval, startOfMonth, endOfMonth } from "date-fns";
import { de } from "date-fns/locale";
import { Copy } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CopyShiftModal = ({ open, onClose, shift, shiftType, worker, currentMonth, onSuccess }) => {
  const [selectedDates, setSelectedDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copyMode, setCopyMode] = useState('weekdays');
  const [selectedWeekdays, setSelectedWeekdays] = useState([]);

  const weekdayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const allDaysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  useEffect(() => {
    if (open) {
      setSelectedDates([]);
      setSelectedWeekdays([]);
    }
  }, [open]);

  const handleCopy = async () => {
    if (selectedDates.length === 0) {
      toast.error("Bitte wählen Sie mindestens ein Datum aus");
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const promises = selectedDates.map(date => {
        const shiftData = {
          worker_id: shift.worker_id,
          date: format(date, 'yyyy-MM-dd'),
          shift_type_id: shift.shift_type_id,
          notes: shift.notes,
          custom_start_time: shift.custom_start_time,
          custom_end_time: shift.custom_end_time
        };
        return axios.post(`${API}/shifts`, shiftData, { headers });
      });

      await Promise.all(promises);
      toast.success(`${selectedDates.length} Schicht(en) erfolgreich kopiert!`);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error("Fehler beim Kopieren der Schichten");
    } finally {
      setLoading(false);
    }
  };

  const handleWeekdayToggle = (dayIndex) => {
    setSelectedWeekdays(prev => 
      prev.includes(dayIndex) ? prev.filter(d => d !== dayIndex) : [...prev, dayIndex]
    );
  };

  const applyWeekdayFilter = () => {
    if (selectedWeekdays.length === 0) {
      toast.error("Bitte wählen Sie mindestens einen Wochentag aus");
      return;
    }
    
    const filteredDays = allDaysInMonth.filter(day => {
      const dayOfWeek = day.getDay();
      return selectedWeekdays.includes(dayOfWeek) && format(day, 'yyyy-MM-dd') !== shift.date;
    });

    setSelectedDates(filteredDays);
    toast.success(`${filteredDays.length} Tage ausgewählt`);
  };

  const toggleDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    if (dateStr === shift.date) return; // Can't copy to same day
    
    setSelectedDates(prev => {
      const exists = prev.find(d => format(d, 'yyyy-MM-dd') === dateStr);
      if (exists) {
        return prev.filter(d => format(d, 'yyyy-MM-dd') !== dateStr);
      } else {
        return [...prev, date];
      }
    });
  };

  const shiftTime = shift.custom_start_time && shift.custom_end_time
    ? `${shift.custom_start_time} - ${shift.custom_end_time}`
    : shiftType ? `${shiftType.start_time} - ${shiftType.end_time}` : '';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="copy-shift-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="w-5 h-5" />
            Schicht kopieren
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-slate-900">Zu kopierende Schicht:</p>
            <p className="text-sm text-slate-700">
              <span className="font-semibold">{worker?.name}</span> - {shiftTime}
            </p>
            <p className="text-xs text-slate-600 mt-1">Original-Datum: {format(new Date(shift.date + 'T00:00:00'), 'dd.MM.yyyy', { locale: de })}</p>
          </div>

          <div className="border-t pt-4">
            <Label className="text-base font-semibold mb-3 block">Zieltage auswählen</Label>
            
            <div className="mb-4 p-3 bg-slate-50 rounded-lg">
              <Label className="text-sm mb-2 block">Wochentage auswählen:</Label>
              <div className="flex flex-wrap gap-2 mb-3">
                {weekdayNames.map((day, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Checkbox
                      id={`weekday-${index}`}
                      checked={selectedWeekdays.includes(index)}
                      onCheckedChange={() => handleWeekdayToggle(index)}
                    />
                    <label htmlFor={`weekday-${index}`} className="text-sm cursor-pointer">
                      {day}
                    </label>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                onClick={applyWeekdayFilter}
                className="w-full"
                size="sm"
              >
                Tage anwenden
              </Button>
            </div>

            <div className="border rounded-lg p-3 max-h-64 overflow-y-auto">
              <div className="grid grid-cols-7 gap-1">
                {allDaysInMonth.map(day => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const isOriginalDate = dateStr === shift.date;
                  const isSelected = selectedDates.find(d => format(d, 'yyyy-MM-dd') === dateStr);
                  
                  return (
                    <button
                      key={dateStr}
                      type="button"
                      onClick={() => toggleDate(day)}
                      disabled={isOriginalDate}
                      className={`p-2 text-sm rounded transition-colors ${
                        isOriginalDate 
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                          : isSelected
                          ? 'bg-blue-600 text-white'
                          : 'bg-white hover:bg-slate-100 border border-slate-200'
                      }`}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>
            </div>

            <p className="text-sm text-slate-600 mt-2">
              {selectedDates.length} Tag(e) ausgewählt
            </p>
          </div>

          <div className="flex gap-2 justify-end pt-2 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              onClick={handleCopy}
              disabled={loading || selectedDates.length === 0}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {loading ? 'Kopiere...' : `${selectedDates.length} Schicht(en) kopieren`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CopyShiftModal;
